import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { ATTACHMENT_TYPES, RELATIONSHIP } from "utils/constants";
import { downloadUploadedDocument } from "utils/helpers/files";
import ActionButtons from "../Components/ActionButtons";
import DataProtection from "../Components/DataProtection";
import Header from "../Components/Header";
import Layout from "../Components/Layout";
import DocumentUpload from "./DocumentUpload";
import validation from "./validation";

function View({
  initialValues,
  onSubmit,
  downloadFile,
  onBack,
  supportingDocuments,
  isRia,
  paymentConditions = {},
  subscription,
  project,
  onFileRemove: handleFileRemove,
  translate,
  professionalSituationConditions = {},
  documentsLoading,
  isSigned = false,
}) {
  const isTax = supportingDocuments.includes(ATTACHMENT_TYPES.PAY_SLIP);
  const isProfessional = supportingDocuments.includes(
    ATTACHMENT_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
  );
  const isEnrollmentCertificate = supportingDocuments.includes(
    ATTACHMENT_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
  );
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = translate || {};

  // Extract conditions from paymentConditions
  const {
    requiresSubscriberPaysButDifferentReceiver = false,
    requiresThirdPartyPayer = false,
    requiresBothThirdParty = false,
    requiresTwoSeparateRibs = false,
  } = paymentConditions;

  // Extract conditions from professionalSituationConditions
  const {
    requiresTerritorialCollectivityAttestation = false,
    requiresCnraclIrcantecAffiliation = false,
  } = professionalSituationConditions;

  // Get related persons (children and spouse) to determine how many social security certificates needed
  const relatedPersons = get(project, "contact.relatedPersons", []).filter(
    (_) => _,
  );
  const hasChildren = !!relatedPersons.filter(
    ({ relationship }) => relationship === RELATIONSHIP.CHILD,
  ).length;
  const hasSpouse = !isEmpty(
    relatedPersons.find(
      ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
    ),
  );

  // Filter initialValues based on professionalSituation to only show relevant documents
  // Always include all documents in initialValues, but we'll control visibility in the UI
  const filteredInitialValues = useMemo(() => {
    if (!initialValues) {
      // Return empty structure if initialValues not loaded yet
      return {
        cin: [],
        paymentAccount: [],
        refundAccount: [],
        socialSecurityCertificate: [],
        payerCin: [],
        receiverCin: [],
        proofOfAddress: [],
        socialSecurityCertificateForChildren: false,
        territorialCollectivityAttestation: [],
        cnraclIrcantecAffiliation: [],
      };
    }

    // Always include all documents from initialValues
    // The filtering for display will be done in the UI based on professionalSituation
    return {
      cin: initialValues.cin || [],
      paymentAccount: initialValues.paymentAccount || [],
      refundAccount: initialValues.refundAccount || [],
      socialSecurityCertificate: initialValues.socialSecurityCertificate || [],
      payerCin: initialValues.payerCin || [],
      receiverCin: initialValues.receiverCin || [],
      proofOfAddress: initialValues.proofOfAddress || [],
      socialSecurityCertificateForChildren:
        initialValues.socialSecurityCertificateForChildren || false,
      // Always include these documents in initialValues, visibility controlled by professionalSituation
      territorialCollectivityAttestation:
        initialValues.territorialCollectivityAttestation || [],
      cnraclIrcantecAffiliation: initialValues.cnraclIrcantecAffiliation || [],
    };
  }, [initialValues]);

  const { isValid, submitForm, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues: {
        cin: filteredInitialValues?.cin || [],
        paymentAccount: filteredInitialValues?.paymentAccount || [],
        refundAccount: filteredInitialValues?.refundAccount || [],
        socialSecurityCertificate:
          filteredInitialValues?.socialSecurityCertificate || [],
        payerCin: filteredInitialValues?.payerCin || [],
        receiverCin: filteredInitialValues?.receiverCin || [],
        proofOfAddress: filteredInitialValues?.proofOfAddress || [],
        socialSecurityCertificateForChildren:
          filteredInitialValues?.socialSecurityCertificateForChildren || false,
        territorialCollectivityAttestation:
          filteredInitialValues?.territorialCollectivityAttestation || [],
        cnraclIrcantecAffiliation:
          filteredInitialValues?.cnraclIrcantecAffiliation || [],
      },
      enableReinitialize: true,
      // validateOnMount: false,
      // validateOnChange: false,
      validateOnBlur: true,
      onSubmit: onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
          returnObjects: true,
        }),
        {
          isTax,
          isRia,
          isProfessional,
          isEnrollmentCertificate,
          requiresSubscriberPaysButDifferentReceiver,
          requiresThirdPartyPayer,
          requiresBothThirdParty,
          requiresTwoSeparateRibs,
          isActive: requiresTerritorialCollectivityAttestation,
          isRetired: requiresCnraclIrcantecAffiliation,
        },
      ),
    });

  // Track the previous conditions to detect actual changes
  const prevConditions = useRef({
    requiresTerritorialCollectivityAttestation,
    requiresCnraclIrcantecAffiliation,
  });
  const isInitialMount = useRef(true);

  // Reset conditional document fields ONLY when conditions actually change
  // (not on initial mount or when returning to the page)
  useEffect(() => {
    // Skip on initial mount - let Formik initialize with values from API
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevConditions.current = {
        requiresTerritorialCollectivityAttestation,
        requiresCnraclIrcantecAffiliation,
      };
      return;
    }

    // Only run if conditions actually changed
    const prevTerritorial =
      prevConditions.current.requiresTerritorialCollectivityAttestation;
    const prevCnracl = prevConditions.current.requiresCnraclIrcantecAffiliation;

    if (
      prevTerritorial === requiresTerritorialCollectivityAttestation &&
      prevCnracl === requiresCnraclIrcantecAffiliation
    ) {
      return;
    }

    // Update the previous values
    prevConditions.current = {
      requiresTerritorialCollectivityAttestation,
      requiresCnraclIrcantecAffiliation,
    };

    // Now handle the actual change
    if (requiresTerritorialCollectivityAttestation) {
      // If territorial attestation is now required, clear CNRACL/IRCANTEC
      setFieldValue("cnraclIrcantecAffiliation", []);
    } else if (requiresCnraclIrcantecAffiliation) {
      // If CNRACL/IRCANTEC is now required, clear territorial attestation
      setFieldValue("territorialCollectivityAttestation", []);
    } else {
      // If neither is required, clear both
      setFieldValue("territorialCollectivityAttestation", []);
      setFieldValue("cnraclIrcantecAffiliation", []);
    }
  }, [
    requiresTerritorialCollectivityAttestation,
    requiresCnraclIrcantecAffiliation,
    setFieldValue,
  ]);

  const createFileRemoveHandler = (fieldName) => async (item) => {
    if (handleFileRemove) {
      await handleFileRemove(fieldName)(item);
      // Remove file from form values immediately
      const currentFiles = values[fieldName] || [];
      const updatedFiles = currentFiles.filter(
        (file) => (file?.uid || file?.id) !== (item?.uid || item?.id),
      );
      setFieldValue(fieldName, updatedFiles);
    }
  };

  const handleDownload = ({ uid, size, base64, contentType, name }) => {
    if (!size) return downloadFile(uid);
    downloadUploadedDocument({
      base64,
      contentType,
      name,
    });
  };

  return (
    <Layout>
      <Header
        title={
          translate?.HEADER?.TITLE ||
          "Dernière chose ! Nous avons besoin de vos pièces justificatives"
        }
        highlight={translate?.HEADER?.HIGHLIGHT || "pièces justificatives"}
        subtitle={
          translate?.HEADER?.SUBTITLE || "Pièces justificatives de l'adhérent"
        }
      />

      <div className="miltis-form">
        <div className="miltis-form__container">
          {/* CA-01: Attestation d'assurance maladie (carte Vitale) - Always required for adherent + beneficiaries */}
          <DocumentUpload
            label={
              translate?.DOCUMENTS?.SOCIAL_SECURITY_CERTIFICATE?.LABEL ||
              "Attestation d'assurance maladie (carte Vitale) du membre participant"
            }
            items={values.socialSecurityCertificate || []}
            onDownload={handleDownload}
            onUpload={(item) => {
              setFieldValue("socialSecurityCertificate", item);
            }}
            onFileRemove={createFileRemoveHandler("socialSecurityCertificate")}
            error={get(formProps.errors, "socialSecurityCertificate")}
            required={true}
            hint={
              translate?.DOCUMENTS?.SOCIAL_SECURITY_CERTIFICATE?.HINT ||
              "Carte Vitale ou attestation de droits"
            }
          />

          {/* CA-02: RIB - At least 1 RIB always required */}
          {requiresTwoSeparateRibs ? (
            // Two distinct RIBs required (payer + receiver)
            <>
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.PAYER_RIB?.LABEL ||
                  "RIB du compte de prélèvement des cotisations"
                }
                items={values.paymentAccount || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("paymentAccount", item);
                }}
                onFileRemove={createFileRemoveHandler("paymentAccount")}
                error={get(formProps.errors, "paymentAccount")}
                required={true}
                hint={
                  translate?.DOCUMENTS?.PAYER_RIB?.HINT ||
                  "RIB du compte qui sera débité pour les cotisations"
                }
              />
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.RECEIVER_RIB?.LABEL ||
                  "RIB du compte de remboursement des prestations"
                }
                items={values.refundAccount || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("refundAccount", item);
                }}
                onFileRemove={createFileRemoveHandler("refundAccount")}
                error={get(formProps.errors, "refundAccount")}
                required={true}
                hint={
                  translate?.DOCUMENTS?.RECEIVER_RIB?.HINT ||
                  "RIB du compte qui recevra les remboursements"
                }
              />
            </>
          ) : (
            // Standard case: single RIB required (at least 1)
            <DocumentUpload
              label={
                translate?.DOCUMENTS?.PAYMENT_ACCOUNT?.LABEL ||
                "Relevé d'Identité Bancaire (RIB)"
              }
              items={values.paymentAccount || []}
              onDownload={handleDownload}
              onUpload={(item) => {
                setFieldValue("paymentAccount", item);
              }}
              onFileRemove={createFileRemoveHandler("paymentAccount")}
              error={get(formProps.errors, "paymentAccount")}
              required={true}
              hint={
                translate?.DOCUMENTS?.PAYMENT_ACCOUNT?.HINT ||
                "Au minimum 1 RIB"
              }
            />
          )}

          {/* CA-03: Proof of address - Always required */}
          <DocumentUpload
            label={
              translate?.DOCUMENTS?.PROOF_OF_ADDRESS?.LABEL ||
              "Justificatif de domicile de moins de 3 mois"
            }
            items={values.proofOfAddress || []}
            onDownload={handleDownload}
            onUpload={(item) => {
              setFieldValue("proofOfAddress", item);
            }}
            onFileRemove={createFileRemoveHandler("proofOfAddress")}
            error={get(formProps.errors, "proofOfAddress")}
            required={true}
            hint={
              translate?.DOCUMENTS?.PROOF_OF_ADDRESS?.HINT ||
              "Facture d'eau, d'électricité, internet, etc."
            }
          />

          {/* Subscriber pays but doesn't receive - Requires: CIN adherent + CIN receiver + RIB receiver */}
          {requiresSubscriberPaysButDifferentReceiver &&
            !requiresBothThirdParty && (
              <>
                <DocumentUpload
                  label={
                    translate?.DOCUMENTS?.CIN?.LABEL ||
                    "Photocopie de la pièce d'identité du membre participant"
                  }
                  items={values.cin || []}
                  onDownload={handleDownload}
                  onUpload={(item) => {
                    setFieldValue("cin", item);
                  }}
                  onFileRemove={createFileRemoveHandler("cin")}
                  error={get(formProps.errors, "cin")}
                  required={true}
                  hint={
                    translate?.DOCUMENTS?.CIN?.HINT ||
                    "Carte d'identité ou passeport du souscripteur"
                  }
                />
                <DocumentUpload
                  label={
                    translate?.DOCUMENTS?.RECEIVER_CIN?.LABEL ||
                    "Photocopie de la pièce d'identité de la personne qui reçoit les remboursements"
                  }
                  items={values.receiverCin || []}
                  onDownload={handleDownload}
                  onUpload={(item) => {
                    setFieldValue("receiverCin", item);
                  }}
                  onFileRemove={createFileRemoveHandler("receiverCin")}
                  error={get(formProps.errors, "receiverCin")}
                  required={true}
                  hint={
                    translate?.DOCUMENTS?.RECEIVER_CIN?.HINT ||
                    "Carte d'identité ou passeport de la personne qui reçoit les remboursements"
                  }
                />
              </>
            )}

          {/* Third-party payer: Subscriber doesn't pay - Requires: CIN adherent + CIN payer */}
          {requiresThirdPartyPayer && !requiresBothThirdParty && (
            <>
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.CIN?.LABEL ||
                  "Photocopie de la pièce d'identité du membre participant"
                }
                items={values.cin || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("cin", item);
                }}
                onFileRemove={createFileRemoveHandler("cin")}
                error={get(formProps.errors, "cin")}
                required={true}
                hint={
                  translate?.DOCUMENTS?.CIN?.HINT ||
                  "Carte d'identité ou passeport du souscripteur"
                }
              />
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.PAYER_CIN?.LABEL ||
                  "Photocopie de la pièce d'identité de la personne qui paye les cotisations"
                }
                items={values.payerCin || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("payerCin", item);
                }}
                onFileRemove={createFileRemoveHandler("payerCin")}
                error={get(formProps.errors, "payerCin")}
                required={true}
                hint={
                  translate?.DOCUMENTS?.PAYER_CIN?.HINT ||
                  "Carte d'identité ou passeport de la personne qui paie les cotisations"
                }
              />
            </>
          )}

          {/* Subscriber doesn't pay AND doesn't receive - Requires: 3 CINs (adherent + payer + receiver) + 2 RIBs */}
          {requiresBothThirdParty && (
            <>
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.CIN?.LABEL ||
                  "Photocopie de la pièce d'identité du membre participant"
                }
                items={values.cin || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("cin", item);
                }}
                onFileRemove={createFileRemoveHandler("cin")}
                error={get(formProps.errors, "cin")}
                required={true}
                hint={
                  translate?.DOCUMENTS?.CIN?.HINT ||
                  "Carte d'identité ou passeport du souscripteur"
                }
              />
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.PAYER_CIN?.LABEL ||
                  "Photocopie de la pièce d'identité de la personne qui paye les cotisations"
                }
                items={values.payerCin || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("payerCin", item);
                }}
                onFileRemove={createFileRemoveHandler("payerCin")}
                error={get(formProps.errors, "payerCin")}
                required={true}
                hint={
                  translate?.DOCUMENTS?.PAYER_CIN?.HINT ||
                  "Carte d'identité ou passeport de la personne qui paie les cotisations"
                }
              />
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.RECEIVER_CIN?.LABEL ||
                  "Photocopie de la pièce d'identité de la personne qui reçoit les remboursements"
                }
                items={values.receiverCin || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("receiverCin", item);
                }}
                onFileRemove={createFileRemoveHandler("receiverCin")}
                error={get(formProps.errors, "receiverCin")}
                required={true}
                hint={
                  translate?.DOCUMENTS?.RECEIVER_CIN?.HINT ||
                  "Carte d'identité ou passeport de la personne qui reçoit les remboursements"
                }
              />
            </>
          )}

          {/* Checkbox for social security certificate for children */}
          {/* {hasChildren && (
            <Checkbox
              checked={values.socialSecurityCertificateForChildren || false}
              onChange={(checked) =>
                setFieldValue("socialSecurityCertificateForChildren", checked)
              }
              variant="consent"
            >
              <div className="miltis__document-upload--checkbox-content">
                <div className="miltis__document-upload--checkbox-label">
                  {translate?.CHILDREN_CHECKBOX?.LABEL ||
                    "Cochez cette case si cette attestation de droit concerne mon/mes enfant(s)"}
                </div>
                <p className="miltis__document-upload--checkbox-hint">
                  {translate?.CHILDREN_CHECKBOX?.HINT ||
                    "L'attestation doit mentionner les enfants."}
                </p>
              </div>
            </Checkbox>
          )} */}

          {/* CA4: Attestation de la collectivité territoriale - Required when professionalSituation = "En activité" AND profession is public service */}
          {requiresTerritorialCollectivityAttestation && (
            <DocumentUpload
              label={
                translate?.DOCUMENTS?.TERRITORIAL_COLLECTIVITY_ATTESTATION
                  ?.LABEL || "Attestation de la collectivité territoriale"
              }
              items={values.territorialCollectivityAttestation || []}
              onDownload={handleDownload}
              onUpload={(item) => {
                setFieldValue("territorialCollectivityAttestation", item);
              }}
              onFileRemove={createFileRemoveHandler(
                "territorialCollectivityAttestation",
              )}
              error={get(
                formProps.errors,
                "territorialCollectivityAttestation",
              )}
              required={true}
              hint={
                translate?.DOCUMENTS?.TERRITORIAL_COLLECTIVITY_ATTESTATION
                  ?.HINT ||
                "Attestation de la collectivité à laquelle est rattaché l'agent à la date de souscription du contrat"
              }
            />
          )}

          {/* CA5: Justificatif d'affiliation CNRACL ou IRCANTEC - Required when professionalSituation = "Retraité" AND profession is public service */}
          {requiresCnraclIrcantecAffiliation && (
            <DocumentUpload
              label={
                translate?.DOCUMENTS?.CNRACL_IRCANTEC_AFFILIATION?.LABEL ||
                "Justificatif d'affiliation CNRACL ou IRCANTEC"
              }
              items={values.cnraclIrcantecAffiliation || []}
              onDownload={handleDownload}
              onUpload={(item) => {
                setFieldValue("cnraclIrcantecAffiliation", item);
              }}
              onFileRemove={createFileRemoveHandler(
                "cnraclIrcantecAffiliation",
              )}
              error={get(formProps.errors, "cnraclIrcantecAffiliation")}
              required={true}
              hint={
                translate?.DOCUMENTS?.CNRACL_IRCANTEC_AFFILIATION?.HINT ||
                "Justificatif d'affiliation au régime CNRACL ou IRCANTEC"
              }
            />
          )}

          <div className="miltis-form__info">
            <p>
              {translate?.FILE_INFO?.SUPPORTED_FORMATS ||
                "Formats de fichiers pris en charge : JPEG, PNG ou PDF"}
            </p>
            <p>{translate?.FILE_INFO?.MAX_SIZE || "Taille maximale : 5 Mo"}</p>
          </div>
          <ActionButtons
            onNext={submitForm}
            buttonText={translate?.BUTTON?.CONTINUE || "Continuer"}
            onBack={onBack}
            nextProps={{
              disabled: !isValid || !isEmpty(formProps.errors),
              loading: documentsLoading,
            }}
            backProps={{
              disabled: isSigned,
            }}
          />

          <DataProtection />
        </div>
      </div>
    </Layout>
  );
}

export default View;
